// src/DimensionnementModal.js
import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash2, Zap, AlertTriangle } from 'lucide-react';
import { supabase } from './supabaseClient';
import { loadProductImages, getProductImage } from './productImages';

// ── Ensoleillement par région ─────────────────────────────────────────────────
const REGIONS = [
  { nom: 'Dakar',        hps: 6.2 },
  { nom: 'Thiès',        hps: 6.3 },
  { nom: 'Diourbel',     hps: 6.4 },
  { nom: 'Fatick',       hps: 6.2 },
  { nom: 'Kaolack',      hps: 6.5 },
  { nom: 'Kaffrine',     hps: 6.6 },
  { nom: 'Kolda',        hps: 6.1 },
  { nom: 'Louga',        hps: 6.5 },
  { nom: 'Matam',        hps: 6.8 },
  { nom: 'Saint-Louis',  hps: 6.5 },
  { nom: 'Sédhiou',      hps: 6.0 },
  { nom: 'Tambacounda',  hps: 6.7 },
  { nom: 'Ziguinchor',   hps: 5.9 },
  { nom: 'Kédougou',     hps: 6.3 },
];

const APPAREILS_DEFAUT = [
  { id: 'lampe_led',     nom: 'Lampe LED',                     puissance: 10,   heures: 5,  icone: '💡' },
  { id: 'tv_32',         nom: 'TV 32"',                        puissance: 60,   heures: 6,  icone: '📺' },
  { id: 'tv_43',         nom: 'TV 43"',                        puissance: 80,   heures: 6,  icone: '📺' },
  { id: 'frigo',         nom: 'Réfrigérateur',                 puissance: 150,  heures: 24, icone: '🧊' },
  { id: 'congelateur',   nom: 'Congélateur',                   puissance: 200,  heures: 24, icone: '🧊' },
  { id: 'clim_9000',     nom: 'Climatiseur 9000 BTU (0.75CV)', puissance: 900,  heures: 8,  icone: '❄️' },
  { id: 'clim_12000',    nom: 'Climatiseur 12000 BTU (1CV)',   puissance: 1200, heures: 8,  icone: '❄️' },
  { id: 'clim_18000',    nom: 'Climatiseur 18000 BTU (1.5CV)', puissance: 1800, heures: 8,  icone: '❄️' },
  { id: 'clim_24000',    nom: 'Climatiseur 24000 BTU (2CV)',   puissance: 2400, heures: 8,  icone: '❄️' },
  { id: 'ventilateur',   nom: 'Ventilateur',                   puissance: 60,   heures: 8,  icone: '🌀' },
  { id: 'chargeur',      nom: 'Chargeur téléphone',            puissance: 10,   heures: 3,  icone: '📱' },
  { id: 'decodeur',      nom: 'Décodeur TV',                   puissance: 20,   heures: 6,  icone: '📡' },
  { id: 'fer',           nom: 'Fer à repasser',                puissance: 1000, heures: 1,  icone: '👔' },
  { id: 'machine_laver', nom: 'Machine à laver',               puissance: 500,  heures: 1,  icone: '🫧' },
  { id: 'ordinateur',    nom: 'Ordinateur / PC',               puissance: 100,  heures: 6,  icone: '💻' },
  { id: 'pompe_eau',     nom: 'Pompe à eau (surface)',         puissance: 500,  heures: 2,  icone: '💧' },
];

const PALIERS = [500,1000,1500,2000,3000,4000,5000,6000,8000,10000,12000,15000,20000,25000,30000,40000,50000,60000];
const arrondirPalier = (wc) => { for (const p of PALIERS) { if (wc <= p) return p; } return Math.ceil(wc/1000)*1000; };

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n||0));

// ── Règles par défaut (écrasées par Supabase) ─────────────────────────────────
const RULES_DEFAULT = {
  seuil_min_kit_auto: 1000,
  seuil_panneau_625: 27,
  seuil_triphase: 30000,
  panneau_545: { ref: 'JKM545M-72HL4-BDVP', nom: 'Panneau Jinko Tiger Neo 545Wc', puissance: 545, prix: 152220 },
  panneau_625: { ref: 'JKM625N-78HL4-BDVP', nom: 'Panneau Jinko Tiger Neo Bifacial 625Wc', puissance: 625, prix: 174560 },
  batterie_gel: { ref: 'GEL-200AH-12V', nom: 'Batterie SunXellence 200Ah 12V GEL', prix: 235200 },
  batterie_lithium_small: { ref: 'LITH-SRNE-5.12KWH-IP65', nom: 'Batterie SRNE SR-EOS05B-Pro 5.12kWh', prix: 995500 },
  batterie_lithium_large: { ref: 'LITH-SRNE-EOS15B-16.07kWH-IP65', nom: 'Batterie SRNE SR-EOS15B 16.07kWh', prix: 2909300 },
  forfait_installation: [
    { max_wc: 1500, prix: 87350 }, { max_wc: 3000, prix: 124750 },
    { max_wc: 5000, prix: 173120 }, { max_wc: 8000, prix: 198640 },
    { max_wc: 20000, prix: 350000 }, { max_wc: 999999, prix: 500000 },
  ],
  paliers_panneaux: [
    { max_panneaux: 4,  onduleur: 'SRNE HF2430S80-H', nb_onduleurs: 1, tension: 24, nb_gel: 2, nb_lithium: 0 },
    { max_panneaux: 6,  onduleur: 'SRNE HF2430S80-H', nb_onduleurs: 1, tension: 24, nb_gel: 4, nb_lithium: 0 },
    { max_panneaux: 10, onduleur: 'SRNE-HYP-5KW',     nb_onduleurs: 1, tension: 48, nb_gel: 4, nb_lithium: 1 },
    { max_panneaux: 18, onduleur: 'SRNE-HYP-5KW',     nb_onduleurs: 2, tension: 48, nb_gel: 4, nb_lithium: 2 },
    { max_panneaux: 27, onduleur: 'SRNE-HYP-5KW',     nb_onduleurs: 3, tension: 48, nb_gel: 4, nb_lithium: 3 },
    { max_panneaux: 37, onduleur: 'SRNE-HYP-5KW',     nb_onduleurs: 4, tension: 48, nb_gel: 4, nb_lithium: 2 },
    { max_panneaux: 43, onduleur: 'SRNE-HYP-5KW',     nb_onduleurs: 5, tension: 48, nb_gel: 4, nb_lithium: 3 },
    { max_panneaux: 49, onduleur: 'SRNE-HYP-5KW',     nb_onduleurs: 6, tension: 48, nb_gel: 4, nb_lithium: 3 },
  ],
};

const DimensionnementModal = ({ onClose, onGenererDevis }) => {
  const [etape, setEtape] = useState(1);
  const [appareils, setAppareils] = useState([
    { id: Date.now(), appareilId: 'lampe_led', nom: 'Lampe LED', puissance: 10, heures: 5, quantite: 4, icone: '💡' },
  ]);
  const [region, setRegion] = useState('Dakar');
  const [autonomie, setAutonomie] = useState(1);
  const [typeBatterie, setTypeBatterie] = useState('gel');
  const [modeTriphase, setModeTriphase] = useState('sans_batterie');
  const [appareilsSelection, setAppareilsSelection] = useState({});
  const [resultat, setResultat] = useState(null);
  const [onduleursCatalogue, setOnduleursCatalogue] = useState([]);
  const [composantsCatalogue, setComposantsCatalogue] = useState({});
  const [rules, setRules] = useState(RULES_DEFAULT);
  const [alerte, setAlerte] = useState(null);
  const [showIA, setShowIA] = useState(false);
  const [promptIA, setPromptIA] = useState('');
  const [loadingIA, setLoadingIA] = useState(false);
  const [erreurIA, setErreurIA] = useState('');

  // ── Charger règles + onduleurs depuis Supabase ────────────────────────────
  useEffect(() => {
    loadProductImages();
    supabase.from('catalogue_produits').select('*').eq('categorie', 'Onduleurs').eq('actif', true).order('puissance_w')
      .then(({ data }) => setOnduleursCatalogue(data || []));

    // Charger les composants electriques (refs precises) pour lookup nom/prix
    const refsComposants = [
      'BOITE-DERIVATION-IP65', 'KIT-CHEVILLES-VIS', 'CONECT-10',
      'COLLIERS-PLASTIQUES', 'MAST-COLL', 'TUBE-IRL-25',
      'GOULOTTE-80x40', 'GOULOTTE-40x20', 'MODUL-2P40',
      'DISJONCTEUR-DIFF-2P40',
      'ACC-CABLE-SOL-DC-25MM', 'ACC-CABLE-DC-6MM2', 'ACC-CABLE-AC-2X6MM', 'ACC-CABLE-HO7RNF-25MM',
      'DC SUNTREE SHLX-PV 1/1 1000V', 'SUNTREE SHXC-250PV 2P', 'AC SUNTREE mono 230V AC',
      'MC4', 'SUP-ALU-REGL', 'LESTAGE-PV-BETON',
      'JKM545M-72HL4-BDVP', 'JKM625N-78HL4-BDVP',
      'GEL-200AH-12V', 'LITH-SRNE-5.12KWH-IP65', 'LITH-SRNE-EOS15B-16.07kWH-IP65', 'PRESTA-INSTALL-PV', 'CASIER-BATTERIES-48V'
    ];
    supabase.from('catalogue_produits').select('ref, nom, prix').in('ref', refsComposants).eq('actif', true)
      .then(({ data }) => {
        const map = {};
        (data || []).forEach(p => { map[p.ref] = p; });
        setComposantsCatalogue(map);
      });

    supabase.from('dimensionnement_rules').select('cle, valeur')
      .then(({ data }) => {
        if (data && data.length > 0) {
          const r = { ...RULES_DEFAULT };
          data.forEach(row => {
            try { r[row.cle] = typeof row.valeur === 'string' ? JSON.parse(row.valeur) : row.valeur; }
            catch { r[row.cle] = row.valeur; }
          });
          setRules(r);
        }
      });
  }, []);

  // ── Forfait installation ──────────────────────────────────────────────────
  const forfaitInstallation = (puissanceCrete) => {
    const paliers = rules.forfait_installation || RULES_DEFAULT.forfait_installation;
    const palier = paliers.find(p => puissanceCrete <= p.max_wc);
    return palier ? palier.prix : 500000;
  };

  // ── Consommation ─────────────────────────────────────────────────────────
  const consoJournaliere = appareils.reduce((sum, a) => sum + (a.puissance||0)*(a.heures||0)*(a.quantite||1), 0);

  const analyserIA = async () => {
    if (!promptIA.trim()) return;
    setLoadingIA(true); setErreurIA('');
    try {
      const res = await fetch('https://api.groq.com/openai/v1/chat/completions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer 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' },
        body: JSON.stringify({
          model: 'llama-3.3-70b-versatile',
          messages: [{
            role: 'system',
            content: `Tu es un expert en dimensionnement solaire au Sénégal. Analyse la description et retourne UNIQUEMENT un JSON valide:
{"appareils": [{"nom": "string", "puissance": number, "heures": number, "quantite": number, "icone": "emoji"}]}
Appareils: Lampe LED=10W/5h, TV 32"=60W/6h, Réfrigérateur=150W/24h, Clim 9000BTU=900W/8h, Clim 12000BTU=1200W/8h, Clim 18000BTU=1800W/8h, Ventilateur=60W/8h, Ordinateur=100W/6h, Chargeur=10W/3h, Fer=1000W/1h, Pompe eau=500W/2h, Décodeur=20W/6h.`
          }, { role: 'user', content: promptIA }],
          temperature: 0.1, max_tokens: 800
        })
      });
      const data = await res.json();
      let content = data.choices?.[0]?.message?.content || '{}';
      content = content.replace(/```json/g, '').replace(/```/g, '').trim();
      const parsed = JSON.parse(content);
      if (parsed.appareils?.length) {
        setAppareils(parsed.appareils.map(a => ({ id: Date.now()+Math.random(), appareilId: 'custom', nom: a.nom, puissance: a.puissance||100, heures: Math.min(a.heures||4,24), quantite: a.quantite||1, icone: a.icone||'⚡' })));
        setShowIA(false); setPromptIA('');
      }
    } catch(e) { console.error('IA erreur:', e); setErreurIA('Erreur : ' + (e.message || 'Réponse inattendue de l\'IA')); }
    setLoadingIA(false);
  };

  const allerEtape2 = () => {
    const seuil = rules.seuil_min_kit_auto || 1000;
    if (consoJournaliere < seuil) { setAlerte('trop_petit'); return; }
    setAlerte(null); setEtape(2);
  };

  // ── Calcul principal ──────────────────────────────────────────────────────
  const calculer = useCallback(() => {
    const regionData = REGIONS.find(r => r.nom === region) || REGIONS[0];
    const hps = regionData.hps;
    const energiePV = consoJournaliere / 0.85;
    const puissanceBrute = energiePV / hps;
    const puissancePalier = arrondirPalier(puissanceBrute);

    // Choisir panneau
    const seuilP625 = parseInt(rules.seuil_panneau_625) || 27;
    const nbTest545 = Math.ceil(puissancePalier / 545);
    // Enrichit le panneau/batterie choisi avec prix/nom du catalogue (fallback sur rules)
    const ccPB = composantsCatalogue || {};
    const enrichCat = (obj) => obj && ccPB[obj.ref] ? { ...obj, nom: ccPB[obj.ref].nom || obj.nom, prix: ccPB[obj.ref].prix ?? obj.prix } : obj;
    const panneau = enrichCat(nbTest545 > seuilP625 ? rules.panneau_625 : rules.panneau_545);
    const nbPanneaux = Math.ceil(puissancePalier / panneau.puissance);
    const puissanceCrete = nbPanneaux * panneau.puissance;

    if (puissanceCrete > 200000) { setAlerte('industriel'); return; }

    const seuilTriphase = parseInt(rules.seuil_triphase) || 30000;
    const isGrandeInstallation = puissanceCrete > seuilTriphase;

    // Récupérer onduleurs du catalogue
    const cat = onduleursCatalogue.length > 0 ? onduleursCatalogue : [];
    const getOnd = (ref) => cat.find(o => o.ref === ref) || null;

    const hf3k   = getOnd('SRNE HF2430S80-H') || { ref: 'SRNE HF2430S80-H', nom: 'Onduleur SRNE HF 3.3kW 24V', puissance_w: 3300, tension_batterie: 24, prix: 582100 };
    const hyp5k  = getOnd('SRNE-HYP-5KW')     || { ref: 'SRNE-HYP-5KW', nom: 'Onduleur Hybride Monophasé SRNE HYP 5kW 48V', puissance_w: 5000, tension_batterie: 48, prix: 946570 };
    const hesp16 = getOnd('SRNE-HESP-16KW')   || { ref: 'SRNE-HESP-16KW', nom: 'Onduleur Hybride SRNE HESP 16kW', puissance_w: 16000, tension_batterie: 48, prix: 2439710 };
    const hesp18 = getOnd('SRNE-HESP-18KW')   || { ref: 'SRNE-HESP-18KW', nom: 'Onduleur Hybride SRNE HESP 18kW', puissance_w: 18000, tension_batterie: 48, prix: 2839710 };
    const hesp20 = getOnd('SRNE-HESP-20KW')   || { ref: 'SRNE-HESP-20KW', nom: 'Onduleur Hybride SRNE HESP 20kW', puissance_w: 20000, tension_batterie: 48, prix: 3239710 };
    const gw15   = getOnd('GROWATT MID 15KTL3-X')  || { ref: 'GROWATT MID 15KTL3-X', nom: 'Onduleur Triphasé Growatt MID 15KW', puissance_w: 15000, tension_batterie: 0, prix: 2497250 };
    const gw20   = getOnd('GROWATT MID 20KTL3-XH') || { ref: 'GROWATT MID 20KTL3-XH', nom: 'Onduleur Triphasé Growatt MID 20KW', puissance_w: 20000, tension_batterie: 0, prix: 2964840 };
    const gw60   = getOnd('GROWATT MAX 60KTL3-LV') || { ref: 'GROWATT MAX 60KTL3-LV', nom: 'Onduleur Triphasé Growatt MAX 60KW', puissance_w: 60000, tension_batterie: 0, prix: 4625000 };

    let onduleurs = [], batteries = [], tensionSysteme = 48, isTriphase = false;

    const bGel   = enrichCat(rules.batterie_gel           || RULES_DEFAULT.batterie_gel);
    const bLithS = enrichCat(rules.batterie_lithium_small || RULES_DEFAULT.batterie_lithium_small);
    const bLithL = enrichCat(rules.batterie_lithium_large || RULES_DEFAULT.batterie_lithium_large);

    if (isGrandeInstallation) {
      // > 30kW : choix selon modeTriphase
      if (modeTriphase === 'sans_batterie') {
        isTriphase = true;
        const sansBat = [gw15, gw20, gw60].filter(Boolean).sort((a,b) => a.puissance_w - b.puissance_w);
        const ond = sansBat.find(o => o.puissance_w >= puissanceCrete * 1.1);
        if (ond) onduleurs = [{ ...ond, quantite: 1 }];
        else onduleurs = [{ ...gw60, quantite: Math.ceil(puissanceCrete / 55000) }];
        batteries = [];
      } else if (modeTriphase === 'hyp_triphase') {
        // 3-6× HYP 5kW en triphasé
        const nbHyp = Math.min(6, Math.ceil(puissanceCrete / 5000));
        onduleurs = [{ ...hyp5k, quantite: nbHyp }];
        tensionSysteme = 48;
        isTriphase = true;
        if (typeBatterie === 'gel') {
          batteries = [{ ...bGel, quantite: 4 }];
        } else {
          batteries = [{ ...bLithL, quantite: Math.min(3, nbHyp) }];
        }
      } else {
        // HESP triphasé
        const nbHesp = Math.min(9, Math.ceil(puissanceCrete / 20000));
        onduleurs = [{ ...hesp20, quantite: nbHesp }];
        tensionSysteme = 48;
        isTriphase = true;
        if (typeBatterie === 'gel') {
          batteries = [{ ...bGel, quantite: 4 }];
        } else {
          batteries = [{ ...bLithL, quantite: Math.min(4, nbHesp) }];
        }
      }
    } else {
      // ≤ 30kW : logique puissance de pointe foisonnee
      const IDS_FRIGO = ['frigo', 'congelateur'];
      const IDS_CLIM  = ['clim_9000', 'clim_12000', 'clim_18000', 'clim_24000'];
      let pointeFrigo = 0, pointeClim = 0, pointeAutres = 0, nbClims = 0;
      appareils.forEach(a => {
        const puiss = (a.puissance || 0) * (a.quantite || 1);
        const id = a.appareilId || '';
        if (IDS_FRIGO.some(x => id.includes(x))) { pointeFrigo += puiss; }
        else if (IDS_CLIM.some(x => id.includes(x))) { nbClims += (a.quantite || 1); pointeClim += puiss; }
        else { pointeAutres += puiss * 0.70; }
      });
      const hasClim = nbClims > 0;
      if (nbClims > 4) {
        const puissMoyClim = pointeClim / nbClims;
        pointeClim = puissMoyClim * 4;
      }
      const puissePointe = pointeFrigo + pointeClim + pointeAutres;
      if (puissePointe < 2500 && !hasClim) {
        onduleurs = [{ ...hf3k, quantite: 1 }];
        tensionSysteme = 24;
        batteries = typeBatterie === 'gel' ? [{ ...bGel, quantite: nbPanneaux <= 4 ? 2 : 4 }] : [{ ...bLithS, quantite: 1 }];
      } else if (modeTriphase === 'hesp' && nbPanneaux > 27) {
        let hesp = hesp16, nbH = 1;
        if (puissanceCrete > 20000) { hesp = hesp20; nbH = Math.min(2, Math.ceil(puissanceCrete/20000)); }
        else if (puissanceCrete > 18000) { hesp = hesp18; nbH = 1; }
        onduleurs = [{ ...hesp, quantite: nbH }];
        tensionSysteme = 48;
        batteries = typeBatterie === 'gel' ? [{ ...bGel, quantite: 4 }] : [{ ...bLithL, quantite: nbH <= 1 ? 2 : 3 }];
      } else {
        const nbHyp = puissePointe <= 4000 ? 1 : puissePointe <= 8000 ? 2 : puissePointe <= 12000 ? 3 : puissePointe <= 16000 ? 4 : puissePointe <= 20000 ? 5 : 6;
        onduleurs = [{ ...hyp5k, quantite: nbHyp }];
        tensionSysteme = 48;
        if (typeBatterie === 'gel') {
          batteries = [{ ...bGel, quantite: nbHyp <= 1 ? 4 : nbHyp <= 3 ? 4 : 8 }];
        } else {
          batteries = [{ ...bLithS, quantite: nbHyp <= 2 ? 1 : nbHyp <= 4 ? 2 : 3 }];
        }
      }
    }

    // ── Câbles ───────────────────────────────────────────────────────────────
    const cableDC6  = ((nbPanneaux * 2) + 20) * 2;
    const cableAC6  = 42;
    const cableHO7  = cableDC6;
    const cableDC25 = 10;
    const installation = forfaitInstallation(puissanceCrete);
    const nbCoffretDC = onduleurs.reduce((s, o) => s + (o.quantite || 1), 0);

    // Helper pour lire prix/nom depuis composantsCatalogue avec fallback hardcode
    const cc = composantsCatalogue || {};
    const getC = (ref, fallbackNom, fallbackPrix) => ({
      nom: cc[ref]?.nom || fallbackNom,
      prix: cc[ref]?.prix ?? fallbackPrix,
    });
    const cBoite  = getC('BOITE-DERIVATION-IP65', 'Lot de 2 boîtes de dérivation IP65', 2200);
    const cChev   = getC('KIT-CHEVILLES-VIS', 'Kit chevilles et vis n8', 2150);
    const cConect = getC('CONECT-10', 'Lot 10 connecteurs borne de serrage 12 plots', 3500);
    const cColl   = getC('COLLIERS-PLASTIQUES', 'Lot colliers attaches plastiques', 9250);
    const cMast   = getC('MAST-COLL', 'Lot 6pcs Mastic-colle Sikaflex', 47250);
const cInst = getC('PRESTA-INSTALL-PV', 'Installation DC/AC, Tests et Mise en service', 35000);
        const cTube   = getC('TUBE-IRL-25', 'Tube IRL conduit rigide diam.25 L10m', 14400);
    const cGoul80 = getC('GOULOTTE-80x40', 'Goulotte PVC 80x40mm blanc (L3m)', 13950);
    const cGoul40 = getC('GOULOTTE-40x20', 'Goulotte PVC 40x20mm blanc (L3m)', 12300);
    const cModul  = getC('MODUL-2P40', 'Modulaire Schneider 2P 25A 500V (SFT232) IP20', 41200);
    const cDisj   = getC('DISJONCTEUR-DIFF-2P40', 'Disjoncteur Schneider Différentiel 2P40 6kA', 30840);
    const cCableDC25 = getC('ACC-CABLE-SOL-DC-25MM', 'Câble solaire DC 25mm²', 3380);
    const cCableDC6  = getC('ACC-CABLE-DC-6MM2', 'Câble DC 6mm²', 2930);
    const cCableAC6  = getC('ACC-CABLE-AC-2X6MM', 'Câble électrique AC 2×6mm²', 1850);
    const cCableHO7  = getC('ACC-CABLE-HO7RNF-25MM', 'Câble HO7RN-F 2.5mm²', 420);
    const cCoffDC    = getC('DC SUNTREE SHLX-PV 1/1 1000V', 'Coffret protection DC SUNTREE', 164340);
    const cCoffBat   = getC('SUNTREE SHXC-250PV 2P', 'Coffret protection Batteries SUNTREE', 135200);
    const cCoffAC    = getC('AC SUNTREE mono 230V AC', 'Coffret protection AC SUNTREE', 131490);
    const cMC4       = getC('MC4', 'Connecteurs MC4 (paires)', 3230);
    const cSupAlu    = getC('SUP-ALU-REGL', 'Kit supports PV réglables (jambes)', 25120);
    const cLestage   = getC('LESTAGE-PV-BETON', 'Dispositif lestage supports PV', 6410);

    const composantsFixes = [
      { ref: 'ACC-CABLE-SOL-DC-25MM', nom: cCableDC25.nom,              quantite: cableDC25,  unite: 'm', prix: cCableDC25.prix,    modifiable: true,  cle: 'cableDC25' },
      { ref: 'ACC-CABLE-DC-6MM2',  nom: cCableDC6.nom,                       quantite: cableDC6,   unite: 'm', prix: cCableDC6.prix,    modifiable: true,  cle: 'cableDC6' },
      { ref: 'ACC-CABLE-AC-2X6MM',   nom: cCableAC6.nom,          quantite: cableAC6,   unite: 'm', prix: cCableAC6.prix,    modifiable: true,  cle: 'cableAC6' },
      { ref: 'ACC-CABLE-HO7RNF-25MM',  nom: cCableHO7.nom,               quantite: cableHO7,   unite: 'm', prix: cCableHO7.prix,     modifiable: true,  cle: 'cableHO7' },
      { ref: 'TUBE-IRL-25',    nom: cTube.nom,    quantite: Math.ceil(nbPanneaux / 5), prix: cTube.prix,    modifiable: true,  cle: 'tubeIRL' },
      { ref: 'GOULOTTE-80x40', nom: cGoul80.nom,  quantite: 2,                      prix: cGoul80.prix,   modifiable: true,  cle: 'goulotte80' },
      { ref: 'GOULOTTE-40x20', nom: cGoul40.nom,  quantite: 2,                      prix: cGoul40.prix,   modifiable: true,  cle: 'goulotte40' },
      { ref: 'DC SUNTREE SHLX-PV 1/1 1000V',     nom: cCoffDC.nom,        quantite: nbCoffretDC,            prix: cCoffDC.prix,  modifiable: false },
      { ref: 'SUNTREE SHXC-250PV 2P',    nom: cCoffBat.nom, quantite: 1,                      prix: cCoffBat.prix,  modifiable: false },
      { ref: 'AC SUNTREE mono 230V AC',     nom: cCoffAC.nom,        quantite: 1,                      prix: cCoffAC.prix,  modifiable: false },
      { ref: 'MODUL-2P40',     nom: cModul.nom,   quantite: 1,                      prix: cModul.prix,    modifiable: true,  cle: 'modulaire' },
      { ref: 'DISJONCTEUR-DIFF-2P40', nom: cDisj.nom, quantite: 1,                  prix: cDisj.prix,     modifiable: true,  cle: 'disjoncteur' },
      { ref: 'BOITE-DERIVATION-IP65', nom: cBoite.nom, quantite: 1,                 prix: cBoite.prix,    modifiable: true,  cle: 'boites' },
      { ref: 'CONECT-10',      nom: cConect.nom,  quantite: 1,                      prix: cConect.prix,   modifiable: true,  cle: 'conect' },
      { ref: 'MC4',            nom: cMC4.nom,             quantite: 3,                      prix: cMC4.prix,    modifiable: true,  cle: 'mc4' },
      { ref: 'COLLIERS-PLASTIQUES', nom: cColl.nom, quantite: 1,                   prix: cColl.prix,     modifiable: true,  cle: 'colliers' },
      { ref: 'KIT-CHEVILLES-VIS', nom: cChev.nom, quantite: 1,                     prix: cChev.prix,     modifiable: true,  cle: 'chevilles' },
      { ref: 'MAST-COLL',      nom: cMast.nom,    quantite: 1,                     prix: cMast.prix,     modifiable: true,  cle: 'mastic' },
      { ref: 'LESTAGE-PV-BETON',     nom: cLestage.nom,       quantite: nbPanneaux * 3,prix: cLestage.prix,    modifiable: true,  cle: 'lestage' },
      { ref: 'SUP-ALU-REGL',     nom: cSupAlu.nom,   quantite: nbPanneaux,             prix: cSupAlu.prix,   modifiable: true,  cle: 'supports' },
      { ref: 'PRESTA-INSTALL-PV',   nom: cInst.nom, quantite: 1,             prix: installation, modifiable: true, cle: 'installation' },
    ];

    // Casier batteries si tension 48V et batteries GEL
    if ((tensionSysteme === 48 || tensionSysteme === 24) && typeBatterie === 'gel' && !isGrandeInstallation) {
      const cCasier = getC('CASIER-BATTERIES-48V', 'Casier stockage batteries 24/48V', 165420);
      composantsFixes.splice(0, 0, {
        ref: 'CASIER-BATTERIES-48V', nom: cCasier.nom,
        quantite: 1, prix: cCasier.prix, modifiable: false,
      });
    }

    const typeInstall = isTriphase
      ? (modeTriphase === 'sans_batterie' ? 'Triphasé sans batterie' : 'Triphasé avec batterie')
      : `Autonomie: ${autonomie}j | ${typeBatterie === 'gel' ? 'GEL' : 'Lithium LiFePO4'}`;

    setResultat({
      conso: consoJournaliere, energiePV, hps, region,
      puissanceBrute: Math.round(puissanceBrute),
      puissancePalier, puissanceCrete, nbPanneaux,
      isTriphase, tensionSysteme, typeInstall,
      panneaux: { ...panneau, quantite: nbPanneaux },
      onduleurs, batteries, composantsFixes,
      nbHyp: onduleurs[0]?.ref === 'SRNE-HYP-5KW' ? (onduleurs[0]?.quantite || 1) : 0,
      hasClim: appareils.some(a => ['clim_9000','clim_12000','clim_18000','clim_24000'].includes(a.appareilId)),
    });

    setEtape(3);
  }, [appareils, autonomie, typeBatterie, modeTriphase, region, consoJournaliere, onduleursCatalogue, composantsCatalogue, rules]);

  const updateComposant = (cle, newQty) => setResultat(prev => ({
    ...prev, composantsFixes: prev.composantsFixes.map(c => c.cle === cle ? { ...c, quantite: Math.max(0, newQty) } : c),
  }));
  const updatePrix = (cle, newPrix) => setResultat(prev => ({
    ...prev, composantsFixes: prev.composantsFixes.map(c => c.cle === cle ? { ...c, prix: Math.max(0, newPrix) } : c),
  }));

  // ── Générer devis ─────────────────────────────────────────────────────────
  const genererDevis = () => {
    if (!resultat) return;
    const articles = [];
    let uid = 0;
    articles.push({
      _uid: uid++, _titre: true,
      description: `STARTER KIT™ TAKOUSSANE ENERGY  —  Pnom: ${resultat.puissanceCrete}Wc  |  Région: ${resultat.region} (${resultat.hps}h)  |  Conso: ${Math.round(resultat.conso)} Wh/j  |  ${resultat.typeInstall}`,
      quantity: null, unitPrice: null, image: null,
    });
    articles.push({ _uid: uid++, description: `${composantsCatalogue[resultat.panneaux.ref]?.nom || resultat.panneaux.nom}\nRef: ${resultat.panneaux.ref}`, quantity: resultat.panneaux.quantite, unitPrice: resultat.panneaux.prix, image: getProductImage(resultat.panneaux.ref) });
    resultat.onduleurs.forEach(o => articles.push({ _uid: uid++, description: `${(onduleursCatalogue.find(x => x.ref === o.ref)?.nom || o.nom)}\nRef: ${o.ref}`, quantity: o.quantite, unitPrice: o.prix, image: getProductImage(o.ref) }));
    resultat.batteries.forEach(b => articles.push({ _uid: uid++, description: `${composantsCatalogue[b.ref]?.nom || b.nom}\nRef: ${b.ref}`, quantity: b.quantite, unitPrice: b.prix, image: getProductImage(b.ref) }));
    resultat.composantsFixes.forEach(c => {
      if (c.quantite > 0) articles.push({ _uid: uid++, description: c.nom + (c.unite ? ` (${c.quantite}${c.unite})` : ''), quantity: c.unite ? 1 : c.quantite, unitPrice: c.unite ? c.quantite * c.prix : c.prix, image: getProductImage(c.ref) });
    });
    onGenererDevis(articles);
    onClose();
  };

  const totalResultat = resultat ? (
    resultat.panneaux.quantite * resultat.panneaux.prix +
    resultat.onduleurs.reduce((s,o) => s + o.quantite * o.prix, 0) +
    resultat.batteries.reduce((s,b) => s + b.quantite * b.prix, 0) +
    resultat.composantsFixes.reduce((s,c) => s + c.quantite * c.prix, 0)
  ) : 0;

  // ── Calcul prévisualisation étape 2 ──────────────────────────────────────
  const previewPuissance = () => {
    const r = REGIONS.find(rr => rr.nom === region) || REGIONS[0];
    const puissanceBrute = (consoJournaliere / 0.85) / r.hps;
    const puissancePalier = arrondirPalier(puissanceBrute);
    const seuilP625 = parseInt(rules.seuil_panneau_625) || 27;
    const nb545 = Math.ceil(puissancePalier / 545);
    const panneau = nb545 > seuilP625 ? rules.panneau_625 : rules.panneau_545;
    const nb = Math.ceil(puissancePalier / panneau.puissance);
    const pc = nb * panneau.puissance;
    const seuilTriphase = parseInt(rules.seuil_triphase) || 30000;
    return { puissanceCrete: pc, nbPanneaux: nb, panneau, isGrande: pc > seuilTriphase };
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 70, padding: '16px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '900px', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>

        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #1e40af, #3b82f6)', padding: '20px 24px', borderRadius: '16px 16px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: 'white', fontWeight: 700, fontSize: '18px' }}>⚡ Dimensionnement Kit PV — Starter Kit</div>
            <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '12px', marginTop: '2px' }}>
              {etape === 1 ? 'Étape 1 — Liste des équipements' : etape === 2 ? 'Étape 2 — Options' : 'Étape 3 — Kit dimensionné'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {[1,2,3].map(n => (
              <div key={n} style={{ width: '28px', height: '28px', borderRadius: '50%', background: etape >= n ? 'white' : 'rgba(255,255,255,0.25)', color: etape >= n ? '#1e40af' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700 }}>{n}</div>
            ))}
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', borderRadius: '6px', padding: '6px', marginLeft: '8px', display: 'flex' }}><X size={20} /></button>
          </div>
        </div>

        {/* Corps */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

          {/* ── ÉTAPE 1 ── */}
          {etape === 1 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '15px', color: '#1f2937' }}>Équipements électriques du client</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>Renseignez la liste. Puissance et heures d'utilisation sont pré-remplies.</div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button onClick={() => setShowIA(!showIA)} style={{ background: showIA ? 'linear-gradient(135deg, #7c3aed, #a855f7)' : 'linear-gradient(135deg, #1e40af, #3b82f6)', border: 'none', borderRadius: '8px', padding: '8px 14px', color: 'white', fontSize: '13px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 2px 8px rgba(59,130,246,0.4)', transition: 'all 0.2s' }}>
                  ✨ IA
                </button>
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '8px 14px', textAlign: 'right' }}>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>Consommation</div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e40af' }}>{fmt(consoJournaliere)} Wh/j</div>
                  <div style={{ borderLeft: '1px solid #bfdbfe', paddingLeft: '16px' }}>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>Pnom estimé* (Dakar)</div>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#0369a1' }}>~{Math.ceil(arrondirPalier(Math.ceil(consoJournaliere / 0.85 / 6.2)) / 545) * 545} Wc</div>
                    <div style={{ fontSize: '9px', color: '#9ca3af', marginTop: '2px' }}>* base panneau 545Wc</div>
                  </div>
                </div>
                </div>
              </div>

              <AnimatePresence>
                {alerte === 'trop_petit' && (
                  <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <AlertTriangle size={18} color="#f59e0b" style={{ flexShrink: 0, marginTop: '1px' }} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '13px', color: '#92400e', marginBottom: '4px' }}>Consommation trop faible pour un kit automatique</div>
                      <div style={{ fontSize: '12px', color: '#78350f' }}>
                        La consommation de <strong>{fmt(consoJournaliere)} Wh/j</strong> est inférieure au seuil minimum ({rules.seuil_min_kit_auto} Wh/j).
                        Composez ce devis manuellement depuis le <strong>catalogue</strong>.
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Liste appareils */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 80px 80px 70px 40px', gap: '8px', padding: '6px 10px', fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <div>Appareil</div><div style={{ textAlign: 'center' }}>Puissance W</div><div style={{ textAlign: 'center' }}>Heures/j</div><div style={{ textAlign: 'center' }}>Qté</div><div></div>
                </div>
                {appareils.map(a => (
                  <div key={a.id} style={{ display: 'grid', gridTemplateColumns: '2fr 80px 80px 70px 40px', gap: '8px', alignItems: 'center', background: '#f8fafc', borderRadius: '8px', padding: '8px 10px', border: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '18px' }}>{a.icone}</span>
                      <input value={a.nom} onChange={e => setAppareils(prev => prev.map(ap => ap.id === a.id ? { ...ap, nom: e.target.value } : ap))}
                        style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: '6px', padding: '5px 8px', fontSize: '13px', outline: 'none', background: 'white' }} />
                    </div>
                    <input type="number" value={a.puissance} min="1" onChange={e => setAppareils(prev => prev.map(ap => ap.id === a.id ? { ...ap, puissance: parseFloat(e.target.value)||0 } : ap))}
                      style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '5px 8px', fontSize: '13px', outline: 'none', textAlign: 'center', width: '100%' }} />
                    <input type="number" value={a.heures} min="0.5" max="24" step="0.5" onChange={e => setAppareils(prev => prev.map(ap => ap.id === a.id ? { ...ap, heures: parseFloat(e.target.value)||0 } : ap))}
                      style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '5px 8px', fontSize: '13px', outline: 'none', textAlign: 'center', width: '100%' }} />
                    <input type="number" value={a.quantite} min="1" onChange={e => setAppareils(prev => prev.map(ap => ap.id === a.id ? { ...ap, quantite: parseInt(e.target.value)||1 } : ap))}
                      style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '5px 8px', fontSize: '13px', outline: 'none', textAlign: 'center', width: '100%' }} />
                    <button onClick={() => setAppareils(prev => prev.filter(ap => ap.id !== a.id))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>

              {/* Grille sélection appareils */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {showIA && (
                <div style={{ background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)', border: '1px solid #c4b5fd', borderRadius: '12px', padding: '20px', marginBottom: '16px', boxShadow: '0 4px 20px rgba(124,58,237,0.1)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '20px' }}>✨</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '14px', color: '#5b21b6' }}>Décris le projet, l’IA s’occupe du reste</div>
                      <div style={{ fontSize: '11px', color: '#7c3aed', marginTop: '2px' }}>Mentionne les équipements, la région, l’usage — l’IA génère la liste automatiquement</div>
                    </div>
                  </div>
                  <textarea value={promptIA} onChange={e => setPromptIA(e.target.value)} rows={3}
                    placeholder="Ex: Maison 4 pièces à Thiès, 2 climatiseurs 12000 BTU, 1 frigo, éclairage LED dans 5 pièces, télévision..."
                    style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #c4b5fd', borderRadius: '8px', padding: '10px', fontSize: '13px', outline: 'none', background: 'white', resize: 'none', fontFamily: 'inherit', lineHeight: 1.5 }} />
                  <button onClick={analyserIA} disabled={loadingIA || !promptIA.trim()}
                    style={{ marginTop: '10px', width: '100%', padding: '10px', background: loadingIA ? '#a78bfa' : 'linear-gradient(135deg, #7c3aed, #a855f7)', border: 'none', borderRadius: '8px', color: 'white', fontSize: '14px', fontWeight: 700, cursor: loadingIA ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all 0.2s' }}>
                    {loadingIA ? '⏳ Analyse en cours...' : '✨ Analyser et générer la liste'}
                  </button>
                  {erreurIA && <div style={{ marginTop: '8px', color: '#ef4444', fontSize: '12px', textAlign: 'center' }}>{erreurIA}</div>}
                </div>
              )}
                  Appareils à alimenter — cliquez pour sélectionner
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
                  {APPAREILS_DEFAUT.map(app => {
                    const sel = appareilsSelection[app.id] || 0;
                    const selected = sel > 0;
                    return (
                      <div key={app.id} onClick={() => {
                          if (!selected) setAppareilsSelection(prev => ({ ...prev, [app.id]: 1 }));
                          else setAppareilsSelection(prev => { const n = { ...prev }; delete n[app.id]; return n; });
                        }}
                        style={{ border: '2px solid ' + (selected ? '#1e40af' : '#e5e7eb'), borderRadius: '8px', padding: '7px 5px', cursor: 'pointer', background: selected ? '#eff6ff' : 'white', transition: 'all 0.15s', position: 'relative', userSelect: 'none' }}>
                        {selected && (
                          <div style={{ position: 'absolute', top: '3px', right: '3px', width: '13px', height: '13px', borderRadius: '50%', background: '#1e40af', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ color: 'white', fontSize: '8px', fontWeight: 700 }}>✓</span>
                          </div>
                        )}
                        <div style={{ fontSize: '16px', textAlign: 'center', marginBottom: '2px' }}>{app.icone}</div>
                        <div style={{ fontSize: '10px', fontWeight: 600, color: '#1f2937', textAlign: 'center', lineHeight: 1.2 }}>{app.nom}</div>
                        <div style={{ fontSize: '9px', color: '#6b7280', textAlign: 'center', marginTop: '1px' }}>{app.puissance}W·{app.heures}h/j</div>
                        {selected && (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginTop: '5px' }} onClick={e => e.stopPropagation()}>
                            <button onClick={() => setAppareilsSelection(prev => ({ ...prev, [app.id]: Math.max(1, (prev[app.id]||1) - 1) }))}
                              style={{ width: '16px', height: '16px', borderRadius: '3px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontSize: '11px', lineHeight: 1, padding: 0 }}>−</button>
                            <span style={{ fontSize: '11px', fontWeight: 700, color: '#1e40af', minWidth: '14px', textAlign: 'center' }}>{sel}</span>
                            <button onClick={() => setAppareilsSelection(prev => ({ ...prev, [app.id]: (prev[app.id]||1) + 1 }))}
                              style={{ width: '16px', height: '16px', borderRadius: '3px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontSize: '11px', lineHeight: 1, padding: 0 }}>+</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {Object.keys(appareilsSelection).length > 0 && (
                  <button onClick={() => {
                      Object.entries(appareilsSelection).forEach(([id, qty]) => {
                        const app = APPAREILS_DEFAUT.find(a => a.id === id);
                        if (app) for (let i = 0; i < qty; i++) setAppareils(prev => [...prev, { id: Date.now() + Math.random(), appareilId: app.id, nom: app.nom, puissance: app.puissance, heures: app.heures, quantite: 1, icone: app.icone }]);
                      });
                      setAppareilsSelection({});
                    }}
                    style={{ width: '100%', padding: '9px', borderRadius: '8px', background: '#1e40af', color: 'white', border: 'none', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>
                    ✓ Ajouter {Object.values(appareilsSelection).reduce((s,v)=>s+v,0)} appareil(s) sélectionné(s)
                  </button>
                )}
                <button onClick={() => setAppareils(prev => [...prev, { id: Date.now(), appareilId: 'custom', nom: 'Appareil personnalisé', puissance: 100, heures: 4, quantite: 1, icone: '⚡' }])}
                  style={{ background: 'white', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 14px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', width: 'fit-content' }}>
                  <Plus size={14} /> Appareil personnalisé
                </button>
              </div>
            </div>
          )}

          {/* ── ÉTAPE 2 ── */}
          {etape === 2 && (() => {
            const prev = previewPuissance();
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Récap */}
                <div style={{ background: '#eff6ff', borderRadius: '10px', padding: '16px', border: '1px solid #bfdbfe' }}>
                  <div style={{ fontSize: '13px', color: '#1e40af', fontWeight: 600, marginBottom: '4px' }}>📊 Consommation journalière</div>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#1e40af' }}>{fmt(consoJournaliere)} Wh/j</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    Puissance estimée : ~{prev.puissanceCrete} Wc — {prev.nbPanneaux} panneaux {prev.panneau.puissance}Wc
                  </div>
                </div>

                {/* Région */}
                <div>
                  <label style={{ fontWeight: 700, fontSize: '14px', color: '#1f2937', display: 'block', marginBottom: '10px' }}>📍 Région d'installation</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                    {REGIONS.map(r => (
                      <button key={r.nom} onClick={() => setRegion(r.nom)}
                        style={{ padding: '10px 8px', border: `2px solid ${region === r.nom ? '#1e40af' : '#e5e7eb'}`, borderRadius: '8px', background: region === r.nom ? '#eff6ff' : 'white', color: region === r.nom ? '#1e40af' : '#6b7280', fontWeight: region === r.nom ? 700 : 400, cursor: 'pointer', fontSize: '13px', transition: 'all 0.15s', textAlign: 'center' }}>
                        <div>{r.nom}</div>
                        <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>{r.hps}h pic</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Grande installation > 30kW */}
                {prev.isGrande && (
                  <div>
                    <label style={{ fontWeight: 700, fontSize: '14px', color: '#1f2937', display: 'block', marginBottom: '10px' }}>⚡ Configuration (puissance &gt; 30kW)</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {[
                        { val: 'sans_batterie', label: '🔌 Sans batterie — Growatt triphasé', desc: 'Recommandé — injection réseau ou réduction facture' },
                        { val: 'hyp_triphase',  label: '🔋 Avec batterie — HYP 5kW × N (triphasé 3+ unités)', desc: 'Jusqu\'à 6× HYP = 30kW triphasé avec stockage' },
                        { val: 'hesp_triphase', label: '🔋 Avec batterie — HESP triphasé × N', desc: 'HESP 20kW × N unités avec stockage SR-EOS15B' },
                      ].map(opt => (
                        <button key={opt.val} onClick={() => setModeTriphase(opt.val)}
                          style={{ padding: '12px 16px', border: `2px solid ${modeTriphase === opt.val ? '#1e40af' : '#e5e7eb'}`, borderRadius: '10px', background: modeTriphase === opt.val ? '#eff6ff' : 'white', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}>
                          <div style={{ fontWeight: 700, fontSize: '13px', color: modeTriphase === opt.val ? '#1e40af' : '#1f2937' }}>{opt.label}</div>
                          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '3px' }}>{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Option HESP pour 28-49 panneaux */}
                {!prev.isGrande && prev.nbPanneaux > 27 && (
                  <div>
                    <label style={{ fontWeight: 700, fontSize: '14px', color: '#1f2937', display: 'block', marginBottom: '10px' }}>⚡ Configuration onduleur (15-30kW)</label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      {[
                        { val: 'hyp', label: '🔋 HYP 5kW × N', desc: 'Privilégié (stock) — jusqu\'à 6 unités' },
                        { val: 'hesp', label: '🔋 HESP 16/18/20kW', desc: 'Option — onduleur monobloc' },
                      ].map(opt => (
                        <button key={opt.val} onClick={() => setModeTriphase(opt.val)}
                          style={{ flex: 1, padding: '12px', border: `2px solid ${modeTriphase === opt.val ? '#1e40af' : '#e5e7eb'}`, borderRadius: '10px', background: modeTriphase === opt.val ? '#eff6ff' : 'white', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}>
                          <div style={{ fontWeight: 700, fontSize: '13px', color: modeTriphase === opt.val ? '#1e40af' : '#1f2937' }}>{opt.label}</div>
                          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '3px' }}>{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Autonomie */}
                {!prev.isGrande && (
                  <div>
                    <label style={{ fontWeight: 700, fontSize: '14px', color: '#1f2937', display: 'block', marginBottom: '10px' }}>🔋 Autonomie souhaitée (sans soleil)</label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      {[1,2,3].map(j => (
                        <button key={j} onClick={() => setAutonomie(j)}
                          style={{ flex: 1, padding: '12px', border: `2px solid ${autonomie === j ? '#1e40af' : '#e5e7eb'}`, borderRadius: '10px', background: autonomie === j ? '#eff6ff' : 'white', color: autonomie === j ? '#1e40af' : '#6b7280', fontWeight: autonomie === j ? 700 : 400, cursor: 'pointer', fontSize: '14px', transition: 'all 0.15s' }}>
                          {j} jour{j > 1 ? 's' : ''}
                          {j === 1 && <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>Recommandé</div>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Type batterie */}
                {!prev.isGrande && prev.nbPanneaux > 6 && (
                  <div>
                    <label style={{ fontWeight: 700, fontSize: '14px', color: '#1f2937', display: 'block', marginBottom: '10px' }}>🔌 Type de batterie</label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button onClick={() => setTypeBatterie('gel')}
                        style={{ flex: 1, padding: '14px', border: `2px solid ${typeBatterie === 'gel' ? '#1e40af' : '#e5e7eb'}`, borderRadius: '10px', background: typeBatterie === 'gel' ? '#eff6ff' : 'white', color: typeBatterie === 'gel' ? '#1e40af' : '#6b7280', fontWeight: typeBatterie === 'gel' ? 700 : 400, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}>
                        <div style={{ fontSize: '14px' }}>🔋 Batterie GEL</div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>SunXellence™ 200Ah 12V · Par défaut</div>
                      </button>
                      <button onClick={() => setTypeBatterie('lithium')}
                        style={{ flex: 1, padding: '14px', border: `2px solid ${typeBatterie === 'lithium' ? '#1e40af' : '#e5e7eb'}`, borderRadius: '10px', background: typeBatterie === 'lithium' ? '#eff6ff' : 'white', color: typeBatterie === 'lithium' ? '#1e40af' : '#6b7280', fontWeight: typeBatterie === 'lithium' ? 700 : 400, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}>
                        <div style={{ fontSize: '14px' }}>⚡ Lithium LiFePO4</div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>SRNE SR-SE05B / SR-EOS15B · Sur demande</div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── ÉTAPE 3 ── */}
          {etape === 3 && resultat && (
            <div>
              <div style={{ background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)', border: '1px solid #6ee7b7', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                <div style={{ fontWeight: 700, fontSize: '14px', color: '#065f46', marginBottom: '10px' }}>
                  ✅ Kit dimensionné — STARTER KIT™ {resultat.puissanceCrete}Wc
                  {resultat.isTriphase && <span style={{ marginLeft: '8px', fontSize: '12px', background: '#0369a1', color: 'white', borderRadius: '6px', padding: '2px 8px' }}>Triphasé</span>}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                  {[
                    { label: 'Consommation',    value: fmt(resultat.conso) + ' Wh/j' },
                    { label: 'Région',          value: resultat.region + ' (' + resultat.hps + 'h)' },
                    { label: 'Puissance crête', value: resultat.puissanceCrete + ' Wc' },
                    { label: 'Panneaux',        value: resultat.nbPanneaux + ' × ' + resultat.panneaux.puissance + 'Wc' },
                  ].map(item => (
                    <div key={item.label} style={{ background: 'white', borderRadius: '8px', padding: '10px 12px', border: '1px solid #a7f3d0' }}>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>{item.label}</div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: '#065f46' }}>{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {resultat.hasClim && resultat.nbHyp > 0 && (
                <div style={{ background: 'white', borderLeft: '4px solid #f59e0b', borderRadius: '8px', padding: '12px 16px', marginBottom: '4px', display: 'flex', gap: '12px', alignItems: 'flex-start', textAlign: 'left', border: '1px solid #e5e7eb', borderLeftColor: '#f59e0b', borderLeftWidth: '4px' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: '1px' }}>
                    <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <div style={{ fontSize: '12.5px', color: '#374151', lineHeight: '1.6', textAlign: 'left' }}>
                    <span style={{ fontWeight: 700, color: '#1f2937' }}>Climatiseur(s) détecté(s) — vérifier les pics de démarrage.</span><br/>
                    Si le client signale des micro-coupures au démarrage des compresseurs de climatisation, passer à <strong>{resultat.nbHyp + 1}× HYP 5kW</strong> en modifiant la quantité onduleur ci-dessous.
                  </div>
                </div>
              )}

              <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px', padding: '0 4px' }}>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 110px', gap: '8px', alignItems: 'center', background: '#fef3c7', borderRadius: '8px', padding: '10px 12px', border: '1px solid #fde68a' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>🔆 {resultat.panneaux.nom}</div>
                  <div style={{ textAlign: 'center', fontSize: '14px', fontWeight: 700 }}>{resultat.panneaux.quantite}</div>
                  <div style={{ textAlign: 'right', fontSize: '13px', color: '#6b7280' }}>{fmt(resultat.panneaux.prix)} F</div>
                  <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: 700, color: '#1e40af' }}>{fmt(resultat.panneaux.quantite * resultat.panneaux.prix)} F</div>
                </div>
                {resultat.onduleurs.map((o,i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 110px', gap: '8px', alignItems: 'center', background: '#f0f9ff', borderRadius: '8px', padding: '10px 12px', border: '1px solid #bae6fd' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>⚡ {o.nom}</div>
                    <input type="number" min="1" value={o.quantite} onChange={(e) => { const qty = parseInt(e.target.value)||1; setResultat(prev => ({ ...prev, onduleurs: prev.onduleurs.map((ond,j) => j===i ? {...ond, quantite: qty} : ond) })); }} style={{ border: '1px solid #bae6fd', borderRadius: '6px', padding: '4px', fontSize: '14px', fontWeight: 700, textAlign: 'center', width: '100%', background: '#f0f9ff', outline: 'none' }} />
                    <div style={{ textAlign: 'right', fontSize: '13px', color: '#6b7280' }}>{fmt(o.prix)} F</div>
                    <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: 700, color: '#1e40af' }}>{fmt(o.quantite * o.prix)} F</div>
                  </div>
                ))}
                {resultat.batteries.map((b,i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 110px', gap: '8px', alignItems: 'center', background: '#f0fdf4', borderRadius: '8px', padding: '10px 12px', border: '1px solid #bbf7d0' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>🔋 {b.nom}</div>
                    <input type="number" min="1" value={b.quantite} onChange={(e) => { const qty = parseInt(e.target.value)||1; setResultat(prev => ({ ...prev, batteries: prev.batteries.map((bat,j) => j===i ? {...bat, quantite: qty} : bat) })); }} style={{ border: '1px solid #bbf7d0', borderRadius: '6px', padding: '4px', fontSize: '14px', fontWeight: 700, textAlign: 'center', width: '100%', background: '#f0fdf4', outline: 'none' }} />
                    <div style={{ textAlign: 'right', fontSize: '13px', color: '#6b7280' }}>{fmt(b.prix)} F</div>
                    <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: 700, color: '#1e40af' }}>{fmt(b.quantite * b.prix)} F</div>
                  </div>
                ))}
                {resultat.composantsFixes.map((c,i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 110px', gap: '8px', alignItems: 'center', background: i%2===0 ? '#f8fafc' : 'white', borderRadius: '8px', padding: '8px 12px', border: '1px solid #f3f4f6' }}>
                    <div style={{ fontSize: '12px', color: '#374151' }}>🔧 {c.nom}</div>
                    {c.modifiable ? (
                      <input type="number" value={c.quantite} min="0" onChange={e => updateComposant(c.cle, parseFloat(e.target.value)||0)}
                        style={{ border: '1px solid #e5e7eb', borderRadius: '4px', padding: '4px', fontSize: '13px', textAlign: 'center', width: '100%', outline: 'none' }} />
                    ) : (
                      <div style={{ textAlign: 'center', fontSize: '13px', fontWeight: 600 }}>{c.quantite}</div>
                    )}
                    {c.modifiable ? (
                      <input type="number" value={c.prix} min="0" onChange={e => updatePrix(c.cle, parseFloat(e.target.value)||0)}
                        style={{ border: '1px solid #e5e7eb', borderRadius: '4px', padding: '4px', fontSize: '12px', textAlign: 'right', width: '100%', outline: 'none' }} />
                    ) : (
                      <div style={{ textAlign: 'right', fontSize: '12px', color: '#6b7280' }}>{fmt(c.prix)} F</div>
                    )}
                    <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: 600, color: '#1e40af' }}>{fmt(c.quantite * c.prix)} F</div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: '16px', background: '#1e40af', borderRadius: '10px', padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>Total kit estimé</span>
                <span style={{ color: 'white', fontWeight: 800, fontSize: '20px' }}>{fmt(totalResultat)} FCFA</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderRadius: '0 0 16px 16px' }}>
          <div>
            {etape > 1 && (
              <button onClick={() => setEtape(etape-1)}
                style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 18px', fontSize: '13px', cursor: 'pointer', fontWeight: 500 }}>
                ← Retour
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={onClose} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 18px', fontSize: '13px', cursor: 'pointer', fontWeight: 500 }}>Fermer</button>
            {etape === 1 && (
              <button onClick={allerEtape2} disabled={appareils.length === 0 || consoJournaliere === 0}
                style={{ background: appareils.length > 0 && consoJournaliere > 0 ? '#1e40af' : '#e5e7eb', color: appareils.length > 0 && consoJournaliere > 0 ? 'white' : '#9ca3af', border: 'none', borderRadius: '8px', padding: '8px 20px', fontSize: '13px', fontWeight: 600, cursor: appareils.length > 0 ? 'pointer' : 'default' }}>
                Suivant →
              </button>
            )}
            {etape === 2 && (
              <button onClick={calculer}
                style={{ background: '#1e40af', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Zap size={14} /> Dimensionner
              </button>
            )}
            {etape === 3 && (
              <button onClick={genererDevis}
                style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                📋 Générer le devis
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default DimensionnementModal;
